import React from 'react';
import type { IconType } from 'react-icons';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface DateInputProps {
  name?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: IconType;
  className?: string;
  containerClassName?: string;
  required?: boolean;
  disabled?: boolean;
}

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      name,
      value,
      onChange,
      placeholder = 'DD/MM/YYYY',
      icon: Icon,
      className,
      containerClassName,
      required,
      disabled,
    },
    forwardedRef,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const isIOS = React.useMemo(() => {
      if (typeof navigator === 'undefined') return false;
      const ua = navigator.userAgent || '';
      return (
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      );
    }, []);

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
      },
      [onChange],
    );

    const showPlaceholder = isIOS && !value && !disabled;

    return (
      <div className={cn('relative flex items-center group min-w-0', containerClassName)}>
        {Icon && (
          <div className="absolute left-3 text-zinc-500 group-focus-within:text-white transition-colors">
            <Icon size={18} />
          </div>
        )}
        {showPlaceholder && (
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-y-0 flex items-center text-zinc-500 text-[14px]',
              Icon ? 'left-10' : 'left-4',
            )}
          >
            {placeholder}
          </span>
        )}
        <input
          ref={(node) => {
            (inputRef as any).current = node;
            if (typeof forwardedRef === 'function') {
              forwardedRef(node);
            } else if (forwardedRef) {
              (forwardedRef as any).current = node;
            }
          }}
          name={name}
          type="date"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          required={required}
          disabled={disabled}
          className={cn(
            'w-full bg-zinc-900/50 border border-zinc-700 text-white rounded-lg py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 placeholder:text-zinc-600 scheme-dark',
            Icon ? 'pl-10 pr-4' : 'px-4',
            showPlaceholder ? 'text-transparent caret-transparent' : '',
            className,
          )}
        />
      </div>
    );
  },
);

DateInput.displayName = 'DateInput';
